import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import api, { zar } from "@/lib/api";
import { EbikLogo } from "@/components/EbikLogo";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function PayLink() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/public/pay/${id}?t=${encodeURIComponent(t)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This payment link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [id, t]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/pay/${id}/payfast`, { origin_url: window.location.origin, t });
      const form = document.createElement("form");
      form.method = "POST"; form.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => {
        const inp = document.createElement("input"); inp.type = "hidden"; inp.name = k; inp.value = v; form.appendChild(inp);
      });
      document.body.appendChild(form); form.submit();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/30 p-6">
      <EbikLogo />
      <div className="mt-8 w-full max-w-md rounded-2xl border border-border bg-card p-8" data-testid="paylink-card">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" /></div>
        ) : error ? (
          <p className="text-center text-red-600" data-testid="paylink-error">{error}</p>
        ) : info.status === "Paid" ? (
          <div className="text-center">
            <ShieldCheck className="mx-auto text-emerald-600" size={40} />
            <h1 className="mt-3 text-xl font-heading font-extrabold">Already paid</h1>
            <p className="mt-2 text-sm text-muted-foreground">Invoice {info.invoice_number} has already been settled. Thank you.</p>
          </div>
        ) : (
          <div>
            <p className="eyebrow text-primary">Secure Payment · PayFast</p>
            <h1 className="mt-2 text-2xl font-heading font-extrabold">Pay your invoice</h1>
            <div className="mt-5 rounded-xl bg-muted/50 p-5 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Invoice</span><span className="font-mono font-semibold">{info.invoice_number}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Account</span><span className="font-semibold">{info.customer_name}</span></div>
              <div className="flex justify-between items-center"><span className="text-muted-foreground text-sm">Status</span><StatusBadge status={info.status} /></div>
              <div className="flex justify-between border-t border-border pt-3 mt-2"><span className="font-heading font-bold">Amount due</span><span className="font-heading font-extrabold text-primary text-lg">{zar(info.total_zar)}</span></div>
            </div>
            <Button onClick={pay} disabled={paying} className="mt-6 w-full bg-primary hover:bg-red-700 h-12" data-testid="paylink-pay">
              {paying ? "Redirecting to PayFast…" : `Pay ${zar(info.total_zar)}`}
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">You'll be redirected to PayFast's secure checkout.</p>
          </div>
        )}
      </div>
    </div>
  );
}
